import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { Bell, CheckCheck, Info, AlertTriangle, CheckCircle, XCircle, X, TrendingUp, TrendingDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import notificacoesService from '../../services/notificacoes';
import orcamentoNotificationsService from '../../services/orcamentoNotifications';
import Button from '../ui/Button';
const NotificacoesDropdown = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('geral');
    const dropdownRef = useRef(null);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    // Query para buscar notificações gerais
    const { data: notificacoes = [], isLoading } = useQuery({
        queryKey: ['notificacoes'],
        queryFn: () => notificacoesService.listar(),
        refetchInterval: 60000 // Atualizar a cada minuto
    });
    // Query para contar não lidas gerais
    const { data: naoLidasCount = 0 } = useQuery({
        queryKey: ['notificacoes', 'nao-lidas'],
        queryFn: () => notificacoesService.contarNaoLidas(),
        refetchInterval: 60000
    });
    // Query para buscar notificações de orçamentos aprovados
    const { data: orcamentosAprovados = [] } = useQuery({
        queryKey: ['orcamento-notifications', 'aprovados'],
        queryFn: () => orcamentoNotificationsService.listarAprovados(),
        refetchInterval: 60000
    });
    // Query para buscar notificações de orçamentos rejeitados
    const { data: orcamentosRejeitados = [] } = useQuery({
        queryKey: ['orcamento-notifications', 'rejeitados'],
        queryFn: () => orcamentoNotificationsService.listarRejeitados(),
        refetchInterval: 60000
    });
    // Query para contar orçamentos aprovados não lidos
    const { data: aprovadosNaoLidos = 0 } = useQuery({
        queryKey: ['orcamento-notifications', 'aprovados', 'count'],
        queryFn: () => orcamentoNotificationsService.contarNaoLidasAprovados(),
        refetchInterval: 60000
    });
    // Query para contar orçamentos rejeitados não lidos
    const { data: rejeitadosNaoLidos = 0 } = useQuery({
        queryKey: ['orcamento-notifications', 'rejeitados', 'count'],
        queryFn: () => orcamentoNotificationsService.contarNaoLidasRejeitados(),
        refetchInterval: 60000
    });
    // Fechar dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    const handleNotificacaoClick = async (notificacao) => {
        // Marcar como lida
        if (!notificacao.lida) {
            await notificacoesService.marcarComoLida(notificacao.id);
            queryClient.invalidateQueries({ queryKey: ['notificacoes'] });
            queryClient.invalidateQueries({ queryKey: ['notificacoes', 'nao-lidas'] });
        }
        // Navegar para o link se existir
        if (notificacao.link) {
            navigate(notificacao.link);
            setIsOpen(false);
        }
    };
    const handleOrcamentoNotificacaoClick = async (notificacao) => {
        // Marcar como lida
        if (!notificacao.lida) {
            await orcamentoNotificationsService.marcarComoLida(notificacao.id);
            queryClient.invalidateQueries({ queryKey: ['orcamento-notifications'] });
        }
        // Navegar para o link se existir
        if (notificacao.link) {
            navigate(notificacao.link);
            setIsOpen(false);
        }
    };
    const handleMarcarTodasLidas = async () => {
        await notificacoesService.marcarTodasComoLidas();
        queryClient.invalidateQueries({ queryKey: ['notificacoes'] });
        queryClient.invalidateQueries({ queryKey: ['notificacoes', 'nao-lidas'] });
    };
    const getIconByTipo = (tipo) => {
        switch (tipo) {
            case 'info':
                return _jsx(Info, { className: "h-4 w-4 text-blue-500" });
            case 'sucesso':
                return _jsx(CheckCircle, { className: "h-4 w-4 text-green-500" });
            case 'aviso':
                return _jsx(AlertTriangle, { className: "h-4 w-4 text-yellow-500" });
            case 'erro':
                return _jsx(XCircle, { className: "h-4 w-4 text-red-500" });
            default:
                return _jsx(Info, { className: "h-4 w-4 text-gray-500" });
        }
    };
    const formatarData = (dataStr) => {
        const data = new Date(dataStr);
        const agora = new Date();
        const diffMs = agora.getTime() - data.getTime();
        const diffHoras = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDias = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        if (diffHoras < 1) {
            const diffMinutos = Math.floor(diffMs / (1000 * 60));
            return `Há ${diffMinutos} minuto${diffMinutos !== 1 ? 's' : ''}`;
        }
        else if (diffHoras < 24) {
            return `Há ${diffHoras} hora${diffHoras !== 1 ? 's' : ''}`;
        }
        else if (diffDias < 7) {
            return `Há ${diffDias} dia${diffDias !== 1 ? 's' : ''}`;
        }
        else {
            return data.toLocaleDateString('pt-PT');
        }
    };
    // Calcular total de notificações não lidas
    const totalNaoLidas = naoLidasCount + aprovadosNaoLidos + rejeitadosNaoLidos;
    // Obter lista de notificações baseada na aba ativa
    const getNotificacoesAtivas = () => {
        switch (activeTab) {
            case 'aprovados':
                return orcamentosAprovados;
            case 'rejeitados':
                return orcamentosRejeitados;
            default:
                return notificacoes;
        }
    };
    const notificacoesAtivas = getNotificacoesAtivas();
    return (_jsxs("div", { className: "relative", ref: dropdownRef, children: [_jsxs(Button, { variant: "ghost", size: "sm", className: "relative", onClick: () => setIsOpen(!isOpen), children: [_jsx(Bell, { className: "h-5 w-5" }), totalNaoLidas > 0 && (_jsx("span", { className: "absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full flex items-center justify-center text-xs text-white", children: totalNaoLidas > 9 ? '9+' : totalNaoLidas }))] }), isOpen && (_jsxs("div", { className: "absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50", children: [_jsxs("div", { className: "p-4 border-b border-gray-200", children: [_jsxs("div", { className: "flex items-center justify-between mb-3", children: [_jsx("h3", { className: "text-lg font-semibold text-gray-900", children: "Notifica\u00E7\u00F5es" }), _jsxs("div", { className: "flex items-center space-x-2", children: [totalNaoLidas > 0 && (_jsxs("button", { onClick: handleMarcarTodasLidas, className: "text-xs text-blue-600 hover:text-blue-700 flex items-center space-x-1", children: [_jsx(CheckCheck, { className: "h-3 w-3" }), _jsx("span", { children: "Marcar todas como lidas" })] })), _jsx("button", { onClick: () => setIsOpen(false), className: "text-gray-400 hover:text-gray-600", children: _jsx(X, { className: "h-4 w-4" }) })] })] }), _jsxs("div", { className: "flex space-x-1 bg-gray-100 rounded-lg p-1", children: [_jsxs("button", { onClick: () => setActiveTab('geral'), className: `flex-1 flex items-center justify-center space-x-1 px-3 py-2 rounded-md text-xs font-medium transition-colors ${activeTab === 'geral'
                                            ? 'bg-white text-gray-900 shadow-sm'
                                            : 'text-gray-600 hover:text-gray-900'}`, children: [_jsx(Bell, { className: "h-3 w-3" }), _jsx("span", { children: "Geral" }), naoLidasCount > 0 && (_jsx("span", { className: "bg-blue-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs", children: naoLidasCount > 9 ? '9+' : naoLidasCount }))] }), _jsxs("button", { onClick: () => setActiveTab('aprovados'), className: `flex-1 flex items-center justify-center space-x-1 px-3 py-2 rounded-md text-xs font-medium transition-colors ${activeTab === 'aprovados'
                                            ? 'bg-white text-gray-900 shadow-sm'
                                            : 'text-gray-600 hover:text-gray-900'}`, children: [_jsx(TrendingUp, { className: "h-3 w-3" }), _jsx("span", { children: "Aprovados" }), aprovadosNaoLidos > 0 && (_jsx("span", { className: "bg-green-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs", children: aprovadosNaoLidos > 9 ? '9+' : aprovadosNaoLidos }))] }), _jsxs("button", { onClick: () => setActiveTab('rejeitados'), className: `flex-1 flex items-center justify-center space-x-1 px-3 py-2 rounded-md text-xs font-medium transition-colors ${activeTab === 'rejeitados'
                                            ? 'bg-white text-gray-900 shadow-sm'
                                            : 'text-gray-600 hover:text-gray-900'}`, children: [_jsx(TrendingDown, { className: "h-3 w-3" }), _jsx("span", { children: "Rejeitados" }), rejeitadosNaoLidos > 0 && (_jsx("span", { className: "bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs", children: rejeitadosNaoLidos > 9 ? '9+' : rejeitadosNaoLidos }))] })] })] }), _jsx("div", { className: "max-h-96 overflow-y-auto", children: isLoading ? (_jsx("div", { className: "p-4 text-center", children: _jsx("div", { className: "animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" }) })) : notificacoesAtivas.length === 0 ? (_jsxs("div", { className: "p-8 text-center", children: [_jsx(Bell, { className: "h-12 w-12 text-gray-300 mx-auto mb-2" }), _jsx("p", { className: "text-gray-500", children: "Nenhuma notifica\u00E7\u00E3o" })] })) : (_jsx("div", { className: "divide-y divide-gray-100", children: notificacoesAtivas.map((notificacao) => (_jsx("div", { onClick: () => {
                                    if (activeTab === 'geral') {
                                        handleNotificacaoClick(notificacao);
                                    }
                                    else {
                                        handleOrcamentoNotificacaoClick(notificacao);
                                    }
                                }, className: `p-4 hover:bg-gray-50 cursor-pointer transition-colors ${!notificacao.lida ? 'bg-blue-50/50' : ''}`, children: _jsxs("div", { className: "flex items-start space-x-3", children: [_jsx("div", { className: "flex-shrink-0 mt-1", children: getIconByTipo(notificacao.tipo) }), _jsx("div", { className: "flex-1 min-w-0", children: _jsxs("div", { className: "flex items-start justify-between", children: [_jsxs("div", { className: "flex-1", children: [_jsx("p", { className: `text-sm font-medium text-gray-900 ${!notificacao.lida ? 'font-semibold' : ''}`, children: notificacao.titulo }), _jsx("p", { className: "text-sm text-gray-600 mt-1", children: notificacao.mensagem }), activeTab !== 'geral' && notificacao.valorTotal && (_jsxs("p", { className: "text-sm font-medium text-green-600 mt-1", children: ["\u20AC", notificacao.valorTotal?.toFixed(2)] })), _jsx("p", { className: "text-xs text-gray-400 mt-1", children: formatarData(notificacao.data) })] }), !notificacao.lida && (_jsx("div", { className: `w-2 h-2 rounded-full flex-shrink-0 mt-2 ${activeTab === 'aprovados' ? 'bg-green-500' :
                                                            activeTab === 'rejeitados' ? 'bg-red-500' : 'bg-blue-500'}` }))] }) })] }) }, notificacao.id))) })) }), notificacoesAtivas.length > 0 && (_jsx("div", { className: "p-3 border-t border-gray-200", children: _jsx("button", { onClick: () => {
                                if (activeTab === 'geral') {
                                    navigate('/notificacoes');
                                }
                                else {
                                    navigate('/orcamentos');
                                }
                                setIsOpen(false);
                            }, className: "text-sm text-blue-600 hover:text-blue-700 w-full text-center", children: activeTab === 'geral'
                                ? 'Ver todas as notificações'
                                : `Ver todos os orçamentos ${activeTab}` }) }))] }))] }));
};
export default NotificacoesDropdown;
